import { useState, useEffect, useRef, useCallback } from 'react';
import generationStatusService, {
  GenerationType,
  GenerationStatus,
} from '../services/api/generationStatusService';

interface UseGenerationPollingOptions {
  /** Polling interval in ms (default: 3000) */
  interval?: number;
  /** Whether polling is enabled (default: true) */
  enabled?: boolean;
  /** Callback when a job completes */
  onComplete?: (status: GenerationStatus) => void;
  /** Callback when a job fails */
  onFailed?: (status: GenerationStatus) => void;
}

/**
 * Unified polling hook for any generation type.
 *
 * Polls a single job by type + id, returning its normalized status.
 * Automatically stops polling when the job reaches a terminal state.
 *
 * Usage:
 *   const { status, progress, step, isGenerating } = useGenerationPolling('music', projectId);
 */
export function useGenerationPolling(
  type: GenerationType,
  id: string | null,
  options: UseGenerationPollingOptions = {}
) {
  const { interval = 3000, enabled = true, onComplete, onFailed } = options;
  const [data, setData] = useState<GenerationStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onCompleteRef = useRef(onComplete);
  const onFailedRef = useRef(onFailed);

  onCompleteRef.current = onComplete;
  onFailedRef.current = onFailed;

  const poll = useCallback(async () => {
    if (!id || !type) return;
    try {
      const status = await generationStatusService.getStatus(type, id);
      setData(status);
      setError(null);

      if (!status.is_generating) {
        if (status.status === 'completed') {
          onCompleteRef.current?.(status);
        } else if (status.status === 'failed') {
          onFailedRef.current?.(status);
        }
      }
    } catch (err) {
      setError('Failed to fetch status');
    }
  }, [type, id]);

  useEffect(() => {
    if (!id || !enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Initial fetch
    poll();

    // Start polling
    intervalRef.current = setInterval(poll, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [id, enabled, interval, poll]);

  // Stop polling when terminal state reached
  useEffect(() => {
    if (data && !data.is_generating && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [data]);

  return {
    data,
    status: data?.status ?? null,
    progress: data?.progress ?? 0,
    step: data?.step ?? null,
    isGenerating: data?.is_generating ?? false,
    error: data?.error ?? error,
    refetch: poll,
  };
}

/**
 * Hook to poll all active generation jobs for the current user.
 *
 * Usage:
 *   const { activeJobs } = useActiveGenerations({ interval: 5000 });
 */
export function useActiveGenerations(options: { interval?: number; enabled?: boolean } = {}) {
  const { interval = 5000, enabled = true } = options;
  const [activeJobs, setActiveJobs] = useState<GenerationStatus[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchActive = useCallback(async () => {
    try {
      const jobs = await generationStatusService.getActiveJobs();
      setActiveJobs(jobs);
    } catch {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    fetchActive();
    intervalRef.current = setInterval(fetchActive, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, interval, fetchActive]);

  // Stop polling if no active jobs
  useEffect(() => {
    if (activeJobs.length === 0 && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [activeJobs]);

  return { activeJobs, refetch: fetchActive };
}
